
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { type CrawlResult, type Page } from '@/types/crawler';
import AnalysisEngine from './deep-analysis/AnalysisEngine';
import { AnalysisControls } from './deep-analysis/AnalysisControls';
import { LoadingState } from './deep-analysis/LoadingState';
import { ErrorDisplay } from './deep-analysis/ErrorDisplay';
import { AnalysisTabsDisplay } from './deep-analysis/AnalysisTabsDisplay';
import { TooltipProvider } from '@/components/ui/tooltip';
import { FileText, Zap } from 'lucide-react';

interface DeepPageAnalysisWidgetProps {
  result: CrawlResult | null;
  selectedPage: string | null;
}

const DeepPageAnalysisWidget: React.FC<DeepPageAnalysisWidgetProps> = ({
  result,
  selectedPage
}) => {
  const [urlInput, setUrlInput] = useState(selectedPage || '');
  const [selectedPageObj, setSelectedPageObj] = useState<Page | null>(null);
  const [activeTab, setActiveTab] = useState('page-details');
  
  // Update URL input and find page object when selectedPage prop changes
  useEffect(() => {
    if (selectedPage) {
      setUrlInput(selectedPage);
      findAndSetPageObject(selectedPage);
    }
  }, [selectedPage, result]);

  // Find and set the page object based on URL
  const findAndSetPageObject = (url: string) => {
    if (!result || !result.pages) {
      console.warn("No crawl results available");
      return;
    }
    
    const pageObj = result.pages.find(p => p.url === url);
    if (pageObj) {
      setSelectedPageObj(pageObj);
      console.log("Found page object for URL:", url);
    } else {
      // If page object is not found in crawl results, create a minimal one
      console.warn("Page not found in crawl results, creating synthetic object for:", url);
      const syntheticPageObj: Page = {
        url: url,
        content: "",
        metadata: {
          title: "Unknown page",
          description: ""
        },
        headings: [],
        links: [],
        images: [],
        scripts: [],
        stylesheets: [],
        schema: []
      };
      
      setSelectedPageObj(syntheticPageObj);
      toast.info("Page not found in initial crawl. Attempting analysis anyway.");
    }
  };

  // Handle URL input changes and analyze button click
  const handleUrlChange = (newUrl: string) => {
    setUrlInput(newUrl);
    findAndSetPageObject(newUrl);
  };

  return (
    <TooltipProvider>
      <div className="space-y-6 animate-fade-in" id="deep-analysis" data-testid="deep-analysis-widget">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 shadow-lg shadow-teal-500/20">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">
            Page Analysis
          </span>
          <div className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 text-teal-500">
            <span className="flex items-center">
              <Zap className="h-3 w-3 mr-1" />
              AI Powered
            </span>
          </div>
        </h2>

        <AnalysisEngine 
          selectedPage={selectedPage}
          pageObject={selectedPageObj}
        >
          {({
            isAnalyzing,
            analysisComplete,
            pageAnalysisResult,
            analysisError,
            hasError,
            errorMessage,
            handleAnalyze,
            handleErrorRetry
          }) => (
            <div className="space-y-6">
              <AnalysisControls
                urlInput={urlInput}
                setUrlInput={handleUrlChange}
                handleAnalyze={() => {
                  if (selectedPageObj) {
                    console.log("Analyzing page:", selectedPageObj.url);
                    handleAnalyze(selectedPageObj);
                  } else {
                    toast.error("No page selected for analysis");
                  }
                }}
                selectedPageObj={selectedPageObj}
                isAnalyzing={isAnalyzing}
              />

              {hasError && (
                <ErrorDisplay 
                  errorMessage={errorMessage} 
                  onRetry={handleErrorRetry} 
                />
              )}

              {isAnalyzing && <LoadingState />}

              {analysisComplete && pageAnalysisResult && (
                <AnalysisTabsDisplay
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  pageAnalysisResult={pageAnalysisResult}
                  urlInput={urlInput}
                  analysisError={analysisError}
                />
              )}
            </div>
          )}
        </AnalysisEngine>
      </div>
    </TooltipProvider>
  );
};

export default DeepPageAnalysisWidget;
